import { useEffect, useState } from "react";
import { Btn, Eingabe, Feld } from "../ui/primitive.jsx";
import { CSS } from "../stil.js";
import { t } from "../i18n/index.js";

/* ================================================================
   Konto — E-Mail hinterlegen, Passwort ändern, Passwort vergessen.

   Die E-Mail dient allein dazu, ein vergessenes Passwort selbst
   zurücksetzen zu können. Bestandskonten ohne Adresse funktionieren
   unverändert weiter und können sie hier nachtragen.
   ================================================================ */

/* — „Mein Konto“ (Dialog aus dem Menü) ————————————————————————— */

function KontoPanel({ nutzer, onEmailGeaendert, onClose }) {
  const [email, setEmail] = useState(nutzer.email || "");
  const [emailMeldung, setEmailMeldung] = useState(null);
  const [emailFehler, setEmailFehler] = useState(null);
  const [emailLaeuft, setEmailLaeuft] = useState(false);

  const [alt, setAlt] = useState("");
  const [neu, setNeu] = useState("");
  const [neu2, setNeu2] = useState("");
  const [pwMeldung, setPwMeldung] = useState(null);
  const [pwFehler, setPwFehler] = useState(null);
  const [pwLaeuft, setPwLaeuft] = useState(false);


  const speichereEmail = async () => {
    setEmailFehler(null); setEmailMeldung(null);
    const wert = email.trim();
    if (!wert) return setEmailFehler("Bitte eine E-Mail-Adresse angeben.");
    setEmailLaeuft(true);
    try {
      const r = await fetch("/api/konto/email", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: wert }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) { setEmailFehler(json.fehler || "Das hat nicht geklappt."); return; }
      setEmailMeldung("Adresse gespeichert.");
      if (onEmailGeaendert) onEmailGeaendert(json.email);
    } catch (e) {
      setEmailFehler("Der Server ist nicht erreichbar.");
    } finally { setEmailLaeuft(false); }
  };

  const aenderePasswort = async () => {
    setPwFehler(null); setPwMeldung(null);
    if (!alt) return setPwFehler("Bitte das bisherige Passwort angeben.");
    if (neu.length < 8) return setPwFehler("Das neue Passwort braucht mindestens 8 Zeichen.");
    if (neu !== neu2) return setPwFehler("Die neuen Passwörter stimmen nicht überein.");
    setPwLaeuft(true);
    try {
      const r = await fetch("/api/konto/passwort", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ altesPasswort: alt, neuesPasswort: neu }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) { setPwFehler(json.fehler || "Das hat nicht geklappt."); return; }
      setAlt(""); setNeu(""); setNeu2("");
      setPwMeldung("Passwort geändert. Andere Geräte wurden abgemeldet.");
    } catch (e) {
      setPwFehler("Der Server ist nicht erreichbar.");
    } finally { setPwLaeuft(false); }
  };

  return (
    <div className="wl-teil">
      <div className="wl-konto-block">
        <span className="wl-konto-titel">Angemeldet als {nutzer.benutzername}</span>
        <p className="wl-teil-info">
          Hinterlege eine E-Mail-Adresse, damit du dir bei einem vergessenen
          Passwort selbst einen Link zum Zurücksetzen schicken kannst.
        </p>
        <Feld label={t("E-Mail")} breit>
          <Eingabe type="email" value={email} autoComplete="email"
            onChange={(e) => setEmail(e.target.value)} placeholder="name@beispiel.de" />
        </Feld>
        {emailFehler && <p className="wl-fehler">{emailFehler}</p>}
        {emailMeldung && <p className="wl-konto-ok">{emailMeldung}</p>}
        <Btn variante="voll" onClick={speichereEmail} disabled={emailLaeuft}>
          {emailLaeuft ? t("Einen Moment …") : nutzer.email ? t("Adresse aktualisieren") : t("Adresse speichern")}
        </Btn>
      </div>

      <div className="wl-teil-trenner" />

      <div className="wl-konto-block">
        <span className="wl-konto-titel">{t("Passwort ändern")}</span>
        <Feld label={t("Bisheriges Passwort")} breit>
          <Eingabe type="password" value={alt} autoComplete="current-password"
            onChange={(e) => setAlt(e.target.value)} />
        </Feld>
        <Feld label={t("Neues Passwort")} breit>
          <Eingabe type="password" value={neu} autoComplete="new-password"
            onChange={(e) => setNeu(e.target.value)} />
        </Feld>
        <Feld label={t("Neues Passwort wiederholen")} breit>
          <Eingabe type="password" value={neu2} autoComplete="new-password"
            onChange={(e) => setNeu2(e.target.value)} />
        </Feld>
        {pwFehler && <p className="wl-fehler">{pwFehler}</p>}
        {pwMeldung && <p className="wl-konto-ok">{pwMeldung}</p>}
        <Btn variante="voll" onClick={aenderePasswort} disabled={pwLaeuft}>
          {pwLaeuft ? t("Einen Moment …") : t("Passwort ändern")}
        </Btn>
      </div>

      <div className="wl-teil-fuss">
        <Btn variante="linie" onClick={onClose}>{t("Schließen")}</Btn>
      </div>
    </div>
  );
}

/* — „Passwort vergessen“ (in der Anmeldeansicht) ——————————————— */

function VergessenFormular({ onZurueck }) {
  const [kennung, setKennung] = useState("");
  const [meldung, setMeldung] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const absenden = async () => {
    if (laeuft) return;
    setFehler(null); setMeldung(null);
    if (!kennung.trim()) return setFehler("Bitte E-Mail-Adresse oder Benutzernamen angeben.");
    setLaeuft(true);
    try {
      const r = await fetch("/api/passwort-vergessen", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kennung: kennung.trim() }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) { setFehler(json.fehler || "Das hat nicht geklappt."); return; }
      setMeldung(json.hinweis || "Falls ein Konto besteht, ist eine Nachricht unterwegs.");
    } catch (e) {
      setFehler("Der Server ist nicht erreichbar.");
    } finally { setLaeuft(false); }
  };

  return (
    <div className="wl-anmeldefelder" onKeyDown={(e) => { if (e.key === "Enter") absenden(); }}>
      <p className="wl-teil-info">
        Gib deine E-Mail-Adresse oder deinen Benutzernamen an. Wir schicken dir
        einen Link, mit dem du ein neues Passwort setzen kannst.
      </p>
      <Feld label={t("E-Mail oder Benutzername")}>
        <Eingabe value={kennung} autoFocus autoComplete="username"
          onChange={(e) => setKennung(e.target.value)} />
      </Feld>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      {meldung && <p className="wl-konto-ok">{meldung}</p>}
      <Btn variante="voll" className="wl-anmeldeknopf" onClick={absenden} disabled={laeuft}>
        {laeuft ? t("Einen Moment …") : t("Link anfordern")}
      </Btn>
      <button className="wl-textknopf wl-konto-zurueck" onClick={onZurueck}>‹ Zurück zur Anmeldung</button>
    </div>
  );
}

/* — Öffentliche Seite /passwort-neu/<token> ———————————————————— */

function PasswortNeuAnsicht({ token }) {
  const [pruefung, setPruefung] = useState("laden"); /* laden | gueltig | ungueltig */
  const [neu, setNeu] = useState("");
  const [neu2, setNeu2] = useState("");
  const [fehler, setFehler] = useState(null);
  const [fertig, setFertig] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      try {
        const r = await fetch("/api/passwort-neu/" + encodeURIComponent(token));
        const json = await r.json().catch(() => ({}));
        if (aktiv) setPruefung(json.gueltig ? "gueltig" : "ungueltig");
      } catch (e) {
        if (aktiv) setPruefung("ungueltig");
      }
    })();
    return () => { aktiv = false; };
  }, [token]);

  const setzen = async () => {
    if (laeuft) return;
    setFehler(null);
    if (neu.length < 8) return setFehler("Das Passwort braucht mindestens 8 Zeichen.");
    if (neu !== neu2) return setFehler("Die Passwörter stimmen nicht überein.");
    setLaeuft(true);
    try {
      const r = await fetch("/api/passwort-neu", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, passwort: neu }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) { setFehler(json.fehler || "Das hat nicht geklappt."); return; }
      setFertig(true);
    } catch (e) {
      setFehler("Der Server ist nicht erreichbar.");
    } finally { setLaeuft(false); }
  };

  return (
    <div className="wl-root">
      <style>{CSS}</style>
      <div className="wl-anmeldung" onKeyDown={(e) => { if (e.key === "Enter") setzen(); }}>
        <div className="wl-anmeldekarte">
          <span className="wl-marke">WineLake</span>
          <span className="wl-unterzeile">{t("Neues Passwort setzen")}</span>

          {pruefung === "laden" && <p className="wl-teil-info">{t("Link wird geprüft …")}</p>}

          {pruefung === "ungueltig" && (
            <>
              <p className="wl-fehler">
                Dieser Link ist abgelaufen oder wurde bereits verwendet.
              </p>
              <a className="wl-anmeldehinweis" href="/">{t("Zur Anmeldung")}</a>
            </>
          )}

          {pruefung === "gueltig" && !fertig && (
            <>
              <div className="wl-anmeldefelder">
                <Feld label={t("Neues Passwort")}>
                  <Eingabe type="password" value={neu} autoFocus autoComplete="new-password"
                    onChange={(e) => setNeu(e.target.value)} />
                </Feld>
                <Feld label={t("Neues Passwort wiederholen")}>
                  <Eingabe type="password" value={neu2} autoComplete="new-password"
                    onChange={(e) => setNeu2(e.target.value)} />
                </Feld>
              </div>
              {fehler && <p className="wl-fehler">{fehler}</p>}
              <Btn variante="voll" className="wl-anmeldeknopf" onClick={setzen} disabled={laeuft}>
                {laeuft ? t("Einen Moment …") : t("Passwort setzen")}
              </Btn>
            </>
          )}

          {fertig && (
            <>
              <p className="wl-konto-ok">
                Dein Passwort wurde geändert. Alle Geräte wurden abgemeldet.
              </p>
              <a className="wl-anmeldehinweis" href="/">{t("Jetzt anmelden")}</a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { KontoPanel, VergessenFormular, PasswortNeuAnsicht };
